import { FeatureType } from "src/clients/features-control";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { generateFeatureMetadata } from "src/featureCatalogue/Queries/Create/utils";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";

export type ColumnType = "entity" | "feature" | "grouping_key";

export type PreviewData = NonNullable<
  UseFeatureQueryPreview["previewData"]
>["data"];

export const autoAssignColumnTypes = (
  previewData: PreviewData,
  existingTypes?: Record<string, ColumnType>,
): Record<string, ColumnType> => {
  if (!previewData?.length) return {};

  const columnNames = Object.keys(previewData[0] ?? {});
  if (columnNames.length === 0) return {};

  const columnTypes: Record<string, ColumnType> = {};

  columnNames.forEach((column, index) => {
    // Keep existing type if column already has one
    if (existingTypes?.[column]) {
      columnTypes[column] = existingTypes[column];
    } else {
      // Default: first column = entity, rest = features
      columnTypes[column] = index === 0 ? "entity" : "feature";
    }
  });

  // Ensure at least one entity column exists
  const hasEntityColumn = Object.values(columnTypes).some(
    (type) => type === "entity",
  );

  if (!hasEntityColumn && columnNames.length > 0) {
    // Assign first column as entity
    columnTypes[columnNames[0]] = "entity";
  }

  return columnTypes;
};

export const getEntityColumnFromTypes = (
  columnTypes: Record<string, ColumnType>,
) => {
  const entityColumns = Object.entries(columnTypes)
    .filter(([_, type]) => type === "entity")
    .map(([column]) => column);

  return entityColumns[0];
};

export const handleFeatureGenerationFromColumnTypes = (
  columnTypes: Record<string, ColumnType>,
  existingFeatures: QueryFormValues["columns"]["features"],
  windowLength: number | null,
  featureDatatype?: Record<string, FeatureType | undefined>,
) => {
  // Build features array
  const featureColumns = Object.entries(columnTypes)
    .filter(([_, type]) => type === "feature")
    .map(([column]) => column);

  const features = featureColumns.map((col) => {
    const { featureKey, featureName } = generateFeatureMetadata(
      col,
      windowLength ?? undefined,
    );

    // Find existing feature for this column to preserve user edits
    const existingFeature = existingFeatures?.find(
      (f) => f.column_name === col,
    );

    if (existingFeature) {
      // Preserve user-edited fields, only update generated fields
      return {
        ...existingFeature,
        column_name: col,
        feature_key: featureKey,
        name: featureName,
      };
    }

    // New feature - use defaults
    return {
      column_name: col,
      feature_key: featureKey,
      name: featureName,
      feature_type: featureDatatype?.[col],
      feature_default: "None",
      description: "",
    };
  });

  return features;
};
